/**
 * Символы раздела «Сантехника» для плана — по компоненту на элемент каталога.
 * Вид сверху, как на планах санузлов: контур прибора и его характерная деталь.
 * Рисунок задан в собственном viewBox в миллиметрах объекта и растягивается
 * в габарит (UI.SymbolFrame). Цвет — через currentColor.
 */
import { PlumbingId } from "@/shared/catalog";
import { UIKit, type SymbolComponent } from "@/shared/ui";

/** Умывальник */
export const SinkSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 80 60" {...props}>
		<rect
			x="2"
			y="2"
			width="76"
			height="56"
			rx="6"
			fill="none"
			stroke="currentColor"
			strokeWidth="3"
		/>
		<path
			d="M14 44h52c0-18-10-30-26-30S14 26 14 44Z"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<circle cx="40" cy="10" r="3" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Унитаз: бачок у стены, чаша — овал */
export const ToiletSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 370 650" {...props}>
		<rect
			x="20"
			y="10"
			width="330"
			height="170"
			rx="20"
			fill="none"
			stroke="currentColor"
			strokeWidth="14"
		/>
		<ellipse
			cx="185"
			cy="410"
			rx="150"
			ry="225"
			fill="none"
			stroke="currentColor"
			strokeWidth="14"
		/>
		<ellipse cx="185" cy="420" rx="95" ry="150" fill="none" stroke="currentColor" strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Полотенцесушитель-лесенка: спереди — лесенка, сверху — две трубы у стены и перекладина */
export const TowelDryerSymbol: SymbolComponent = ({ facing = "plan", ...props }) =>
	facing === "front" ? (
		<UIKit.SymbolFrame viewBox="0 0 500 800" {...props}>
			<line
				x1="60"
				y1="40"
				x2="60"
				y2="760"
				stroke="currentColor"
				strokeWidth="26"
				strokeLinecap="round"
			/>
			<line
				x1="440"
				y1="40"
				x2="440"
				y2="760"
				stroke="currentColor"
				strokeWidth="26"
				strokeLinecap="round"
			/>
			<path
				d="M60 150h380M60 300h380M60 450h380M60 600h380"
				stroke="currentColor"
				strokeWidth="22"
				strokeLinecap="round"
			/>
		</UIKit.SymbolFrame>
	) : (
		<UIKit.SymbolFrame viewBox="0 0 500 100" {...props}>
			<rect
				x="30"
				y="30"
				width="440"
				height="40"
				rx="20"
				fill="none"
				stroke="currentColor"
				strokeWidth="14"
			/>
			<circle cx="60" cy="50" r="24" fill="currentColor" />
			<circle cx="440" cy="50" r="24" fill="currentColor" />
		</UIKit.SymbolFrame>
	);

/** Ванна: борт, чаша и слив */
export const BathSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1700 750" {...props}>
		<rect
			x="15"
			y="15"
			width="1670"
			height="720"
			rx="120"
			fill="none"
			stroke="currentColor"
			strokeWidth="18"
		/>
		<rect
			x="110"
			y="95"
			width="1480"
			height="560"
			rx="200"
			fill="none"
			stroke="currentColor"
			strokeWidth="12"
		/>
		<circle cx="300" cy="375" r="28" fill="none" stroke="currentColor" strokeWidth="12" />
		<circle cx="150" cy="375" r="18" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Душевая кабина: поддон, сливной трап и распашная дверца по диагонали */
export const ShowerSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 900" {...props}>
		<rect
			x="15"
			y="15"
			width="870"
			height="870"
			rx="40"
			fill="none"
			stroke="currentColor"
			strokeWidth="16"
		/>
		<path d="M15 885L885 15" stroke="currentColor" strokeWidth="10" strokeDasharray="40 30" />
		<circle cx="450" cy="450" r="34" fill="none" stroke="currentColor" strokeWidth="12" />
		<path d="M420 450h60M450 420v60" stroke="currentColor" strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Стиральная машина: корпус и люк */
export const WasherSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 600" {...props}>
		<rect
			x="15"
			y="15"
			width="570"
			height="570"
			rx="24"
			fill="none"
			stroke="currentColor"
			strokeWidth="16"
		/>
		<circle cx="300" cy="320" r="170" fill="none" stroke="currentColor" strokeWidth="14" />
		<circle cx="300" cy="320" r="110" fill="none" stroke="currentColor" strokeWidth="8" />
		<rect
			x="60"
			y="45"
			width="480"
			height="70"
			rx="12"
			fill="none"
			stroke="currentColor"
			strokeWidth="10"
		/>
	</UIKit.SymbolFrame>
);

/** Умывальник с раковиной-чашей: тумба, круглая чаша и смеситель у стены */
export const VesselSinkSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 515" {...props}>
		<rect
			x="8"
			y="8"
			width="584"
			height="499"
			rx="12"
			fill="none"
			stroke="currentColor"
			strokeWidth="12"
		/>
		<circle cx="300" cy="280" r="190" fill="none" stroke="currentColor" strokeWidth="12" />
		<circle cx="300" cy="280" r="22" fill="currentColor" />
		<path d="M300 40v90" stroke="currentColor" strokeWidth="18" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Биде: чаша без бачка, отверстие под смеситель у стены */
export const BidetSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 520 550" {...props}>
		<path
			d="M40 20H480V200C480 420 380 530 260 530S40 420 40 200Z"
			fill="none"
			stroke="currentColor"
			strokeWidth="14"
		/>
		<ellipse
			cx="260"
			cy="300"
			rx="150"
			ry="170"
			fill="none"
			stroke="currentColor"
			strokeWidth="10"
		/>
		<circle cx="260" cy="70" r="20" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Отдельностоящая ванна: овал чаши и напольный смеситель за ней, у стены */
export const FreestandingBathSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1600 1450" {...props}>
		<ellipse
			cx="800"
			cy="830"
			rx="780"
			ry="600"
			fill="none"
			stroke="currentColor"
			strokeWidth="18"
		/>
		<ellipse
			cx="800"
			cy="830"
			rx="660"
			ry="480"
			fill="none"
			stroke="currentColor"
			strokeWidth="10"
		/>
		<circle cx="800" cy="1180" r="26" fill="currentColor" />
		<circle cx="800" cy="90" r="50" fill="none" stroke="currentColor" strokeWidth="14" />
		<path d="M800 140v100" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Шкаф для ванной сверху: корпус и линия дверец спереди */
export const BathCabinetSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 400 350" {...props}>
		<rect
			x="8"
			y="8"
			width="384"
			height="334"
			rx="10"
			fill="none"
			stroke="currentColor"
			strokeWidth="12"
		/>
		<path d="M8 300H392" stroke="currentColor" strokeWidth="8" />
		<path d="M200 300V342" stroke="currentColor" strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Корзина для белья сверху: крышка и ручка по центру */
export const HamperSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="5" />
		<circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" strokeWidth="2" />
		<rect x="38" y="46" width="24" height="8" rx="4" fill="currentColor" />
	</UIKit.SymbolFrame>
);

export const PLUMBING_SYMBOLS: Partial<Record<PlumbingId, SymbolComponent>> = {
	[PlumbingId.Pl330]: SinkSymbol,
	[PlumbingId.Toilet]: ToiletSymbol,
	[PlumbingId.TowelDryer]: TowelDryerSymbol,
	[PlumbingId.Bath]: BathSymbol,
	[PlumbingId.Shower]: ShowerSymbol,
	[PlumbingId.Washer]: WasherSymbol,
	[PlumbingId.VanityVessel]: VesselSinkSymbol,
	[PlumbingId.Bidet]: BidetSymbol,
	[PlumbingId.BathFreestanding]: FreestandingBathSymbol,
	[PlumbingId.BathCabinetTall]: BathCabinetSymbol,
	[PlumbingId.BathCabinet]: BathCabinetSymbol,
	[PlumbingId.ToiletModern]: ToiletSymbol,
	[PlumbingId.ToiletRound]: ToiletSymbol,
	[PlumbingId.VanityCabinetWood]: VesselSinkSymbol,
	[PlumbingId.SinkCountertop]: SinkSymbol,
	[PlumbingId.SinkCurved]: SinkSymbol,
	[PlumbingId.LaundryHamper]: HamperSymbol,
};
